import React, { useState, useEffect, useRef, useMemo } from "react";

import { Modal, Frame, TitleBar, Button, useModal, ModalEvents } from "@react95/core";
import { Progman35, User, Msnsetup1 } from "@react95/icons";

import { useApp } from "../../context/AppContext";
import { useChat } from "../../context/ChatContext";
import { WINDOW_NAMES } from "../../config/const";
import { getModalSizeProps } from "../../config/window";

export const ChatClientDef = {
  id: WINDOW_NAMES.ChatClient,
  title: "eMeSeN Messenger",
  icon: <Msnsetup1 variant="32x32_8" />,
  hasButton: true,
};

export default function ChatClient() {
  const { closeModal } = useApp();
  const { loading, error, contacts, messages, sendingMessage, sendMessage, clearError } =
    useChat();

  const icon = ChatClientDef.icon;
  const sizeProps = useMemo(
    () => getModalSizeProps("450px", "600px", "normal", "270px", "420px"),
    [],
  );

  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // auto-select first contact when contacts load
  useEffect(() => {
    if (contacts.length > 0 && !selectedContactId) {
      setSelectedContactId(contacts[0].id);
    }
  }, [contacts, selectedContactId]);

  // auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sendingMessage, selectedContactId]);

  const handleSendMessage = () => {
    if (!input.trim() || !selectedContactId || sendingMessage) return;

    const msgId = `${Date.now()}-${Math.random()}`;

    // context handles optimistic update
    sendMessage(selectedContactId, input, msgId);
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // prevent default and stop propagation so only this input handles Enter
      e.preventDefault();
      e.stopPropagation();
      handleSendMessage();
    }
  };

  const currentMessages = selectedContactId ? messages[selectedContactId] || [] : [];

  return (
    <Modal
      id={ChatClientDef.id}
      icon={icon}
      title={ChatClientDef.title}
      dragOptions={sizeProps.dragOptions}
      defaultClosed={true}
      titleBarOptions={[
        <Modal.Minimize key="minimize" />,
        <TitleBar.Close key="close" onClick={() => closeModal(ChatClientDef.id)} />,
      ]}
      width={sizeProps.width}
      height={sizeProps.height}
      isResizeable={true}
      minWidth={sizeProps.minWidth}
      minHeight={sizeProps.minHeight}
    >
      {loading && (
        <Frame w="100%" h="100%">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              fontFamily: "MS Sans Serif, Arial, sans-serif",
              fontSize: "12px",
              color: "#666666",
            }}
          >
            Loading contacts...
          </div>
        </Frame>
      )}

      {!loading && (
        <Frame w="100%" h="100%">
          <div
            style={{
              display: "flex",
              height: "100%",
              width: "100%",
            }}
          >
            {/* sidebar - contacts */}
            <Frame
              bgColor="white"
              boxShadow="$in"
              style={{ margin: "8px", width: "125px", overflowY: "auto" }}
            >
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  onClick={() => setSelectedContactId(contact.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 12px",
                    marginBottom: "0",
                    cursor: "pointer",
                    backgroundColor:
                      selectedContactId === contact.id ? "#000080" : "transparent",
                    color: selectedContactId === contact.id ? "#ffffff" : "#000000",
                    fontSize: "11px",
                    borderRadius: "2px",
                    // borderBottom: "1px solid #e6e6e6",
                    transition: "background-color 0.2s",
                    overflow: "hidden",
                  }}
                  title={contact.name}
                >
                  <User variant="32x32_4" style={{ flexShrink: 0 }} />
                  <span
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {contact.name}
                  </span>
                </div>
              ))}
            </Frame>

            {/* chat area */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                padding: "8px",
                gap: "8px",
              }}
            >
              {/* error message display */}
              {error && (
                <div
                  style={{
                    padding: "8px",
                    backgroundColor: "#ff6b6b",
                    color: "#ffffff",
                    border: "1px solid #cc0000",
                    borderRadius: "2px",
                    fontSize: "11px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>{error}</span>
                  <button
                    onClick={() => clearError()}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#ffffff",
                      cursor: "pointer",
                      fontSize: "14px",
                      padding: "0 4px",
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* messages container */}
              <div
                style={{
                  flex: "1 1 0",
                  minHeight: 0,
                  overflow: "auto",
                  padding: "8px",
                  border: "1px inset",
                  backgroundColor: "#ffffff",
                  fontFamily: "MS Sans Serif, Arial, sans-serif",
                  fontSize: "12px",
                  lineHeight: "1.5",
                  userSelect: "text",
                }}
              >
                {/* connection header */}
                <div
                  style={{
                    marginBottom: "12px",
                    paddingBottom: "8px",
                    borderBottom: "1px solid #cccccc",
                    color: "#666666",
                    fontSize: "11px",
                  }}
                >
                  {loading ? (
                    <>Connecting...</>
                  ) : (
                    <>
                      Connected with{" "}
                      <strong>
                        {selectedContactId
                          ? contacts.find((c) => c.id === selectedContactId)?.name
                          : "..."}
                      </strong>
                    </>
                  )}
                </div>

                {currentMessages.length === 0 && !sendingMessage ? (
                  <div
                    style={{
                      color: "#999999",
                      textAlign: "center",
                      paddingTop: "20px",
                    }}
                  >
                    No messages yet. Start chatting!
                  </div>
                ) : (
                  <>
                    {currentMessages.map((msg) => {
                      const senderName =
                        msg.senderId === "user"
                          ? "You"
                          : contacts.find((c) => c.id === msg.senderId)?.name || msg.senderId;
                      return (
                        <div
                          key={msg.id}
                          style={{
                            marginBottom: "8px",
                            wordWrap: "break-word",
                            color: msg.sender === "user" ? "#000080" : "#008000",
                          }}
                        >
                          <span style={{ fontWeight: "bold" }}>{senderName}:</span> {msg.text}
                        </div>
                      );
                    })}
                    {sendingMessage && (
                      <div
                        style={{
                          marginBottom: "8px",
                          color: "#999999",
                          fontStyle: "italic",
                        }}
                      >
                        <span style={{ fontWeight: "bold" }}>
                          {selectedContactId
                            ? contacts.find((c) => c.id === selectedContactId)?.name
                            : "Contact"}
                          :
                        </span>{" "}
                        <span>...</span>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* input row */}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexShrink: 0,
                }}
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  style={{
                    padding: "4px",
                    border: "1px inset",
                    fontFamily: "inherit",
                    flex: 1,
                  }}
                />

                <Button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !selectedContactId}
                >
                  Send
                </Button>
              </div>
            </div>
          </div>
        </Frame>
      )}
    </Modal>
  );
}
